import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';
import { Plus, FileText, Phone, Euro, Clock, CheckCircle, XCircle, Star, ScrollText } from 'lucide-react';
import DashboardHeader from '../components/DashboardHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiError(detail) {
  if (detail == null) return 'Qualcosa è andato storto';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => {
      if (!e) return '';
      if (typeof e === 'string') return e;
      const field = Array.isArray(e.loc) ? e.loc.slice(-1)[0] : '';
      const msg = typeof e.msg === 'string' ? e.msg : JSON.stringify(e);
      return field ? `${field}: ${msg}` : msg;
    }).filter(Boolean).join(' • ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

const ProprietarioDashboard = () => {
  const [requests, setRequests] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [orderRequests, setOrderRequests] = useState([]);
  const [reviewables, setReviewables] = useState([]);
  const [settings, setSettings] = useState(null);
  const [site, setSite] = useState(null);
  const [profile, setProfile] = useState(null);
  const [showCreateRequest, setShowCreateRequest] = useState(false);
  const [showCreateConsultation, setShowCreateConsultation] = useState(false);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [r, c, rv, s, ord, sc, me] = await Promise.all([
        axios.get(`${API}/renovation-requests`, { withCredentials: true }),
        axios.get(`${API}/consultations/me`, { withCredentials: true }),
        axios.get(`${API}/reviews/my-reviewable`, { withCredentials: true }),
        axios.get(`${API}/settings`, { withCredentials: true }),
        axios.get(`${API}/proprietario/order-requests/me`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/site-content`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/proprietario/me`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setRequests(r.data);
      setConsultations(c.data);
      setReviewables(rv.data);
      setSettings(s.data);
      setOrderRequests(ord.data);
      setSite(sc.data);
      setProfile(me.data);
    } catch (e) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#EA580C]" /></div>;
  }

  const pendingReviews = reviewables.filter(r => !r.already_reviewed).length;
  const banner = (k, fb) => (site && site[k]) || fb;

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <DashboardHeader />
      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-[#0F172A] mb-2">Area Proprietario</h1>
          <p className="text-slate-600">Gestisci richieste, consulenze e recensioni</p>
        </div>

        <Card className="mb-6 border-0 rounded-2xl overflow-hidden bg-gradient-to-r from-[#EA580C] to-[#F97316] text-white shadow-lg" data-testid="proprietario-consult-banner">
          <CardContent className="p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0">
              <Phone className="w-8 h-8" />
            </div>
            <div className="flex-1">
              <p className="text-xs font-bold uppercase tracking-widest opacity-90 mb-1">{banner('prop_banner_eyebrow', 'Servizio dedicato')} — solo €{((settings?.consultation_price_cents ?? 5000) / 100).toFixed(2)}</p>
              <h2 className="text-2xl md:text-3xl font-bold mb-2 leading-tight">{banner('prop_banner_title', 'Hai un dubbio? Parla con un esperto al telefono')}</h2>
              <p className="text-white/90 text-sm md:text-base mb-0 md:mb-1 leading-relaxed">
                {banner('prop_banner_description', 'Prenota una consulenza telefonica 1-a-1: preventivi, materiali, tempistiche, burocrazia. Pagamento sicuro tramite bonifico, riceverai una chiamata entro 24h lavorative dall\'accredito.')}
              </p>
            </div>
            <Button
              data-testid="banner-create-consultation-btn"
              onClick={() => setShowCreateConsultation(true)}
              className="bg-white text-[#EA580C] hover:bg-white/90 rounded-lg px-6 py-6 text-base font-semibold shadow-md flex-shrink-0"
            >
              <Phone className="w-5 h-5 mr-2" /> {banner('prop_banner_cta', 'Prenota ora')}
            </Button>
          </CardContent>
        </Card>

        <Tabs defaultValue="requests" className="space-y-6">
          <TabsList className="bg-white border border-slate-200" data-testid="proprietario-tabs">
            <TabsTrigger value="requests" data-testid="tab-requests">Le mie richieste</TabsTrigger>
            <TabsTrigger value="consultations" data-testid="tab-consultations">Consulenze</TabsTrigger>
            <TabsTrigger value="profile" data-testid="tab-profile">Anagrafica</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="tab-reviews">
              Recensioni{pendingReviews > 0 && <span className="ml-2 bg-[#EA580C] text-white text-xs px-2 py-0.5 rounded-full">{pendingReviews}</span>}
            </TabsTrigger>
            <TabsTrigger value="site-reviews" data-testid="tab-site-reviews">Recensisci il sito</TabsTrigger>
          </TabsList>

          <TabsContent value="requests" className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-semibold text-[#0F172A]">Le Mie Richieste</h2>
                <p className="text-sm text-slate-600">Le richieste vengono approvate dall'admin prima della pubblicazione</p>
              </div>
              <Button data-testid="create-request-btn" onClick={() => setShowCreateRequest(true)} className="bg-[#EA580C] hover:bg-[#EA580C]/90 text-white rounded-lg">
                <Plus className="w-4 h-4 mr-2" /> Nuova Richiesta
              </Button>
            </div>

            {requests.length === 0 ? (
              <Card className="bg-white border border-slate-200 rounded-xl">
                <CardContent className="p-12 text-center">
                  <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">Nessuna richiesta ancora. Crea la tua prima richiesta!</p>
                </CardContent>
              </Card>
            ) : (
              requests.map((req) => <RequestCard key={req.id} req={req} />)
            )}
          </TabsContent>

          <TabsContent value="consultations" className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-semibold text-[#0F172A]">Consulenza Telefonica</h2>
                <p className="text-sm text-slate-600">Prenota una consulenza con i nostri esperti - €{((settings?.consultation_price_cents ?? 5000) / 100).toFixed(2)}</p>
              </div>
              <Button data-testid="create-consultation-btn" onClick={() => setShowCreateConsultation(true)} className="bg-[#EA580C] hover:bg-[#EA580C]/90 text-white rounded-lg">
                <Phone className="w-4 h-4 mr-2" /> Prenota Consulenza
              </Button>
            </div>

            {consultations.length === 0 ? (
              <Card className="bg-white border border-slate-200 rounded-xl">
                <CardContent className="p-12 text-center">
                  <Phone className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">Nessuna consulenza prenotata</p>
                </CardContent>
              </Card>
            ) : (
              consultations.map((cons) => <ConsultationCard key={cons.id} cons={cons} onUpdate={loadData} settings={settings} orderRequests={orderRequests} />)
            )}
          </TabsContent>

          <TabsContent value="profile" className="space-y-4">
            <ProprietarioProfileSection profile={profile} onUpdate={loadData} />
          </TabsContent>

          <TabsContent value="reviews" className="space-y-4">
            <div>
              <h2 className="text-2xl font-semibold text-[#0F172A]">Recensioni dei Lavori</h2>
              <p className="text-sm text-slate-600">Lascia una recensione alle imprese che hanno acquistato le tue richieste</p>
            </div>            {reviewables.length === 0 ? (
              <Card className="bg-white border border-slate-200 rounded-xl">
                <CardContent className="p-12 text-center">
                  <Star className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">Nessun lavoro da recensire ancora. Le recensioni saranno disponibili dopo che un'impresa avrà acquistato una tua richiesta.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {reviewables.map((item) => (
                  <Card key={`${item.request_id}-${item.impresa_user_id}`} className="bg-white border border-slate-200 rounded-xl" data-testid={`reviewable-${item.impresa_user_id}-${item.request_id}`}>
                    <CardHeader>
                      <CardTitle className="text-lg text-[#0F172A]">{item.impresa_name}</CardTitle>
                      <CardDescription className="text-sm">Lavoro: {item.request_titolo}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {item.already_reviewed ? (
                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          <CheckCircle className="w-3 h-3" /> Recensione inviata
                        </span>
                      ) : (
                        <Button
                          data-testid={`review-btn-${item.impresa_user_id}-${item.request_id}`}
                          onClick={() => setReviewTarget(item)}
                          className="bg-[#EA580C] hover:bg-[#EA580C]/90 text-white rounded-lg"
                          size="sm"
                        >
                          <Star className="w-4 h-4 mr-2" /> Lascia una recensione
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="site-reviews" className="space-y-4">
            <SiteReviewSection onUpdate={loadData} />
          </TabsContent>
        </Tabs>
      </main>

      <CreateRequestModal open={showCreateRequest} onClose={() => setShowCreateRequest(false)} onSuccess={() => { setShowCreateRequest(false); loadData(); }} />
      <CreateConsultationModal open={showCreateConsultation} onClose={() => setShowCreateConsultation(false)} onSuccess={() => { setShowCreateConsultation(false); loadData(); }} settings={settings} profile={profile} />
      <ReviewModal target={reviewTarget} onClose={() => setReviewTarget(null)} onSuccess={() => { setReviewTarget(null); loadData(); }} />
    </div>
  );
};

const RequestCard = ({ req }) => {
  const statoConfig = {
    pending: { label: 'In Attesa di Approvazione', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
    open: { label: 'Pubblicata', color: 'bg-green-100 text-green-700', icon: CheckCircle },
    closed: { label: 'Chiusa - Max imprese raggiunto', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
    rejected: { label: 'Rifiutata', color: 'bg-red-100 text-red-700', icon: XCircle },
  };
  const config = statoConfig[req.stato] || statoConfig.pending;
  const Icon = config.icon;
  const location = req.citta ? `${req.citta}${req.cap ? ' ' + req.cap : ''}` : (req.location_roma || '');

  return (
    <Card className="bg-white border border-slate-200 rounded-xl shadow-sm" data-testid={`request-card-${req.id}`}>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <CardTitle className="text-2xl font-semibold text-[#0F172A]">{req.descrizione || req.titolo}</CardTitle>
            <CardDescription className="text-slate-600 mt-2">
              {location}
            </CardDescription>
          </div>
          <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${config.color}`}>
            <Icon className="w-3 h-3" /> {config.label}
          </span>
        </div>
      </CardHeader>
      <CardContent>
        {req.indirizzo && <p className="text-sm text-slate-600 mb-2"><strong>Indirizzo:</strong> {req.indirizzo}</p>}
        {req.stato === 'open' && req.purchased_by && (
          <p className="text-sm text-slate-500 mt-3">
            {req.purchased_by.length} imprese hanno acquistato questa richiesta
          </p>
        )}
      </CardContent>
    </Card>
  );
};

const ConsultationCard = ({ cons, onUpdate, settings, orderRequests = [] }) => {
  const myOrder = orderRequests.find(r => r.consultation_id === cons.id && r.stato !== 'rifiutato');
  const handleRequestOrder = async () => {
    try {
      const { data } = await axios.post(`${API}/proprietario/order-requests`,
        { consultation_id: cons.id },
        { withCredentials: true }
      );
      toast.success(`Richiesta ordine ${data.request_number} inviata. L'admin ti emetterà l'ordine con istruzioni bonifico via email.`);
      onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
  };

  return (
    <Card className="bg-white border border-slate-200 rounded-xl" data-testid={`consultation-card-${cons.id}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="flex-1 min-w-0">
            <p className="text-sm text-slate-500">€{((cons.prezzo_cents ?? cons.prezzo * 100) / 100).toFixed(2)}</p>
            <p className="text-[#0F172A]">{cons.descrizione}</p>
            {myOrder && (
              <p className="text-xs mt-2 text-blue-700" data-testid={`consultation-order-status-${cons.id}`}>
                Ordine: <span className="font-mono">{myOrder.order_number || myOrder.request_number}</span> — stato: <strong>{myOrder.stato}</strong>
              </p>
            )}
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-medium flex-shrink-0 ${
            cons.stato === 'paid' ? 'bg-green-100 text-green-700' :
            cons.stato === 'completed' ? 'bg-blue-100 text-blue-700' :
            'bg-yellow-100 text-yellow-700'
          }`}>
            {cons.stato === 'unpaid' ? 'Non pagata' : cons.stato === 'paid' ? 'Pagata - In attesa di chiamata' : 'Completata'}
          </span>
        </div>
        {cons.stato === 'unpaid' && !myOrder && (
          <Button data-testid={`request-order-consultation-${cons.id}`} onClick={handleRequestOrder} size="sm" className="bg-[#EA580C] hover:bg-[#EA580C]/90 text-white">
            <ScrollText className="w-4 h-4 mr-2" /> Richiedi emissione ordine
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

const CreateRequestModal = ({ open, onClose, onSuccess }) => {
  const [nome, setNome] = useState('');
  const [cognome, setCognome] = useState('');
  const [tipologia, setTipologia] = useState('');
  const [descrizione, setDescrizione] = useState('');
  const [immobilePosseduto, setImmobilePosseduto] = useState('');
  const [progettoPronto, setProgettoPronto] = useState('');
  const [emailCliente, setEmailCliente] = useState('');
  const [cellulareCliente, setCellulareCliente] = useState('');
  const [indirizzo, setIndirizzo] = useState('');
  const [cap, setCap] = useState('');
  const [citta, setCitta] = useState('Roma');
  const [loading, setLoading] = useState(false);

  const TIPOLOGIE = [
    { v: 'esterni', l: 'Ristrutturazione esterni' },
    { v: 'appartamento', l: 'Ristrutturazione appartamento' },
    { v: 'bagno', l: 'Ristrutturazione bagno' },
    { v: 'cucina', l: 'Ristrutturazione cucina' },
    { v: 'altro', l: 'Ristrutturazione altro' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!tipologia) { toast.error('Seleziona una tipologia di lavoro'); return; }
    if (descrizione.length > 200) { toast.error('La descrizione deve essere max 200 caratteri'); return; }
    if (immobilePosseduto === '' || progettoPronto === '') { toast.error('Rispondi a tutte le domande'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/renovation-requests`, {
        nome, cognome, descrizione,
        tipologia_lavoro: tipologia,
        immobile_posseduto: immobilePosseduto === 'si',
        progetto_pronto: progettoPronto === 'si',
        email_cliente: emailCliente,
        cellulare_cliente: cellulareCliente,
        indirizzo, cap, citta,
      }, { withCredentials: true });
      toast.success('Richiesta creata! Sarà visibile dopo l\'approvazione dell\'admin.');
      setNome(''); setCognome(''); setTipologia(''); setDescrizione(''); setImmobilePosseduto(''); setProgettoPronto('');
      setEmailCliente(''); setCellulareCliente(''); setIndirizzo(''); setCap(''); setCitta('Roma');
      onSuccess();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'Errore');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg bg-white rounded-2xl border border-slate-200 shadow-2xl max-h-[90vh] overflow-y-auto" data-testid="create-request-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold text-[#0F172A]">Nuova Richiesta di Ristrutturazione</DialogTitle>
          <DialogDescription>I tuoi dati saranno protetti. Le imprese vedranno solo descrizione, CAP e città. I dati personali verranno rivelati solo all'impresa che acquisterà la richiesta.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Nome</Label>
              <Input data-testid="request-nome-input" value={nome} onChange={(e) => setNome(e.target.value)} required className="rounded-lg" />
            </div>
            <div>
              <Label>Cognome</Label>
              <Input data-testid="request-cognome-input" value={cognome} onChange={(e) => setCognome(e.target.value)} required className="rounded-lg" />
            </div>
          </div>
          <div>
            <Label>Tipologia di lavoro</Label>
            <select
              data-testid="request-tipologia-select"
              value={tipologia}
              onChange={(e) => setTipologia(e.target.value)}
              required
              className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
            >
              <option value="">Seleziona...</option>
              {TIPOLOGIE.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
          </div>
          <div>
            <Label>Descrivi di cosa hai bisogno (max 200 caratteri) <span className="text-xs text-slate-500">{descrizione.length}/200</span></Label>
            <Textarea
              data-testid="request-description-input"
              value={descrizione}
              onChange={(e) => setDescrizione(e.target.value.slice(0, 200))}
              maxLength={200}
              required
              rows={4}
              placeholder="Es. Rifacimento completo bagno 6mq con cambio sanitari e box doccia"
              className="rounded-lg"
            />
          </div>
          <div>
            <Label>L'immobile è già in vostro possesso?</Label>
            <div className="flex gap-3 mt-1" data-testid="request-immobile-group">
              <label className={`flex-1 border rounded-lg px-4 py-2 cursor-pointer text-center ${immobilePosseduto === 'si' ? 'border-[#EA580C] bg-[#EA580C]/10 text-[#EA580C]' : 'border-slate-300 text-slate-700'}`}>
                <input type="radio" name="immobile" value="si" checked={immobilePosseduto === 'si'} onChange={(e) => setImmobilePosseduto(e.target.value)} className="sr-only" data-testid="request-immobile-si" /> Sì
              </label>
              <label className={`flex-1 border rounded-lg px-4 py-2 cursor-pointer text-center ${immobilePosseduto === 'no' ? 'border-[#EA580C] bg-[#EA580C]/10 text-[#EA580C]' : 'border-slate-300 text-slate-700'}`}>
                <input type="radio" name="immobile" value="no" checked={immobilePosseduto === 'no'} onChange={(e) => setImmobilePosseduto(e.target.value)} className="sr-only" data-testid="request-immobile-no" /> No
              </label>
            </div>
          </div>
          <div>
            <Label>Avete già un progetto?</Label>
            <div className="flex gap-3 mt-1" data-testid="request-progetto-group">
              <label className={`flex-1 border rounded-lg px-4 py-2 cursor-pointer text-center ${progettoPronto === 'si' ? 'border-[#EA580C] bg-[#EA580C]/10 text-[#EA580C]' : 'border-slate-300 text-slate-700'}`}>
                <input type="radio" name="progetto" value="si" checked={progettoPronto === 'si'} onChange={(e) => setProgettoPronto(e.target.value)} className="sr-only" data-testid="request-progetto-si" /> Sì
              </label>
              <label className={`flex-1 border rounded-lg px-4 py-2 cursor-pointer text-center ${progettoPronto === 'no' ? 'border-[#EA580C] bg-[#EA580C]/10 text-[#EA580C]' : 'border-slate-300 text-slate-700'}`}>
                <input type="radio" name="progetto" value="no" checked={progettoPronto === 'no'} onChange={(e) => setProgettoPronto(e.target.value)} className="sr-only" data-testid="request-progetto-no" /> No
              </label>
            </div>
          </div>
          <div>
            <Label>Email</Label>
            <Input data-testid="request-email-input" type="email" value={emailCliente} onChange={(e) => setEmailCliente(e.target.value)} required className="rounded-lg" />
          </div>
          <div>
            <Label>Cellulare</Label>
            <Input data-testid="request-phone-input" type="tel" value={cellulareCliente} onChange={(e) => setCellulareCliente(e.target.value)} required className="rounded-lg" />
          </div>
          <div>
            <Label>Indirizzo del lavoro</Label>
            <Input data-testid="request-address-input" value={indirizzo} onChange={(e) => setIndirizzo(e.target.value)} placeholder="Es. Via del Corso 123" required className="rounded-lg" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>CAP</Label>
              <Input data-testid="request-cap-input" value={cap} onChange={(e) => setCap(e.target.value)} required maxLength={5} className="rounded-lg" />
            </div>
            <div>
              <Label>Città</Label>
              <Input
                data-testid="request-city-input"
                value="Roma"
                readOnly
                className="rounded-lg bg-slate-50 cursor-not-allowed text-slate-600"
              />
              <p className="text-xs text-slate-500 mt-1">Solo lavori a Roma</p>
            </div>
          </div>
          <Button type="submit" data-testid="create-request-submit-btn" disabled={loading} className="w-full bg-[#EA580C] hover:bg-[#EA580C]/90 text-white rounded-lg">
            {loading ? 'Invio...' : 'Crea Richiesta'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const CreateConsultationModal = ({ open, onClose, onSuccess, settings, profile }) => {
  const [descrizione, setDescrizione] = useState('');
  const [indirizzo, setIndirizzo] = useState('');
  const [civico, setCivico] = useState('');
  const [cap, setCap] = useState('');
  const [citta, setCitta] = useState('');
  const [cf, setCf] = useState('');
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (open && profile) {
      setIndirizzo(profile.indirizzo_residenza || '');
      setCivico(profile.civico || '');
      setCap(profile.cap_residenza || '');
      setCitta(profile.citta_residenza || '');
      setCf(profile.codice_fiscale || '');
    }
  }, [open, profile]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!indirizzo || !civico || !cap || !citta || !cf) {
      toast.error('Compila tutti i campi anagrafici (indirizzo, civico, CAP, città, codice fiscale)');
      return;
    }
    if (cf.length !== 16) {
      toast.error('Il codice fiscale deve essere di 16 caratteri');
      return;
    }
    setLoading(true);
    try {
      const { data: cons } = await axios.post(`${API}/consultations`, {
        descrizione,
        indirizzo_residenza: indirizzo,
        civico,
        cap_residenza: cap,
        citta_residenza: citta,
        codice_fiscale: cf,
      }, { withCredentials: true });
      await axios.post(`${API}/proprietario/order-requests`, { consultation_id: cons.id }, { withCredentials: true });
      toast.success('Consulenza creata e richiesta ordine inviata. Riceverai le istruzioni bonifico via email dopo l\'approvazione admin.');
      setDescrizione('');
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg bg-white rounded-2xl border border-slate-200 shadow-2xl max-h-[90vh] overflow-y-auto" data-testid="create-consultation-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold text-[#0F172A]">Prenota Consulenza Telefonica</DialogTitle>
          <DialogDescription>
            €{((settings?.consultation_price_cents ?? 5000) / 100).toFixed(2)} — Inserisci i tuoi dati anagrafici e descrivi il tuo problema.
            Verrai contattato dopo aver effettuato il bonifico secondo le istruzioni che ti invieremo via email.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <Label>Descrivi il tuo problema</Label>
            <Textarea data-testid="consultation-description-input" value={descrizione} onChange={(e) => setDescrizione(e.target.value)} required rows={4} placeholder="Descrivi brevemente il problema o la domanda..." className="rounded-lg" />
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">Dati anagrafici (obbligatori)</p>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label className="text-xs">Indirizzo di residenza</Label>
                <Input data-testid="consult-indirizzo-input" value={indirizzo} onChange={(e) => setIndirizzo(e.target.value)} required className="rounded-lg" />
              </div>
              <div>
                <Label className="text-xs">Civico</Label>
                <Input data-testid="consult-civico-input" value={civico} onChange={(e) => setCivico(e.target.value)} required className="rounded-lg" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">CAP</Label>
                <Input data-testid="consult-cap-input" value={cap} onChange={(e) => setCap(e.target.value)} required maxLength={5} className="rounded-lg" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Città</Label>
                <Input data-testid="consult-citta-input" value={citta} onChange={(e) => setCitta(e.target.value)} required className="rounded-lg" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Codice Fiscale</Label>
              <Input data-testid="consult-cf-input" value={cf} onChange={(e) => setCf(e.target.value.toUpperCase())} required maxLength={16} placeholder="RSSMRA80A01H501U" className="rounded-lg font-mono" />
            </div>
            <p className="text-xs text-slate-500">Questi dati verranno salvati nella tua anagrafica per le prossime consulenze.</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900">
            💡 Dopo aver creato la consulenza, l'admin emetterà un ordine e riceverai una email con coordinate bancarie e numero d'ordine.
          </div>
          <Button type="submit" data-testid="create-consultation-submit-btn" disabled={loading} className="w-full bg-[#EA580C] hover:bg-[#EA580C]/90 text-white rounded-lg">
            {loading ? 'Invio...' : `Crea consulenza e richiedi ordine — €${((settings?.consultation_price_cents ?? 5000) / 100).toFixed(2)}`}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ProprietarioProfileSection = ({ profile, onUpdate }) => {
  const [name, setName] = useState('');
  const [cognome, setCognome] = useState('');
  const [phone, setPhone] = useState('');
  const [indirizzo, setIndirizzo] = useState('');
  const [civico, setCivico] = useState('');
  const [cap, setCap] = useState('');
  const [citta, setCitta] = useState('');
  const [cf, setCf] = useState('');
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (profile) {
      setName(profile.name || '');
      setCognome(profile.cognome || '');
      setPhone(profile.phone || '');
      setIndirizzo(profile.indirizzo_residenza || '');
      setCivico(profile.civico || '');
      setCap(profile.cap_residenza || '');
      setCitta(profile.citta_residenza || '');
      setCf(profile.codice_fiscale || '');
    }
  }, [profile]);

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(`${API}/proprietario/me`, {
        name, cognome, phone,
        indirizzo_residenza: indirizzo, civico,
        cap_residenza: cap, citta_residenza: citta, codice_fiscale: cf,
      }, { withCredentials: true });
      toast.success('Anagrafica aggiornata');
      onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-semibold text-[#0F172A]">Anagrafica</h2>
        <p className="text-sm text-slate-600">Questi dati saranno utilizzati per le tue richieste di consulenza.</p>
      </div>
      <Card className="bg-white border border-slate-200 rounded-xl" data-testid="proprietario-profile-form">
        <CardContent className="p-6">
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nome</Label>
                <Input data-testid="profile-name-input" value={name} onChange={(e) => setName(e.target.value)} className="rounded-lg" />
              </div>
              <div>
                <Label>Cognome</Label>
                <Input data-testid="profile-cognome-input" value={cognome} onChange={(e) => setCognome(e.target.value)} className="rounded-lg" />
              </div>
            </div>
            <div>
              <Label>Telefono</Label>
              <Input data-testid="profile-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} className="rounded-lg" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label>Indirizzo di residenza</Label>
                <Input data-testid="profile-indirizzo-input" value={indirizzo} onChange={(e) => setIndirizzo(e.target.value)} className="rounded-lg" />
              </div>
              <div>
                <Label>Civico</Label>
                <Input data-testid="profile-civico-input" value={civico} onChange={(e) => setCivico(e.target.value)} className="rounded-lg" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>CAP</Label>
                <Input data-testid="profile-cap-input" value={cap} onChange={(e) => setCap(e.target.value)} maxLength={5} className="rounded-lg" />
              </div>
              <div className="col-span-2">
                <Label>Città</Label>
                <Input data-testid="profile-citta-input" value={citta} onChange={(e) => setCitta(e.target.value)} className="rounded-lg" />
              </div>
            </div>
            <div>
              <Label>Codice Fiscale</Label>
              <Input data-testid="profile-cf-input" value={cf} onChange={(e) => setCf(e.target.value.toUpperCase())} maxLength={16} className="rounded-lg font-mono" />
            </div>
            <Button type="submit" data-testid="profile-save-btn" disabled={loading} className="bg-[#EA580C] hover:bg-[#EA580C]/90 text-white rounded-lg">
              {loading ? 'Salvataggio...' : 'Salva anagrafica'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

const ReviewModal = ({ target, onClose, onSuccess }) => {
  const [rating, setRating] = useState(5);
  const [commento, setCommento] = useState('');
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (target) {
      setRating(5);
      setCommento('');
    }
  }, [target]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!target) return;
    setLoading(true);
    try {
      await axios.post(`${API}/reviews`, {
        impresa_user_id: target.impresa_user_id,
        request_id: target.request_id,
        rating,
        commento,
      }, { withCredentials: true });
      toast.success('Recensione inviata!');
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg bg-white rounded-2xl border border-slate-200 shadow-2xl" data-testid="review-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold text-[#0F172A]">Lascia una Recensione</DialogTitle>
          <DialogDescription>{target?.impresa_name} - {target?.request_titolo}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <Label className="mb-2 block">Valutazione</Label>
            <div className="flex gap-1" data-testid="review-stars">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setRating(n)}
                  className="transition-transform hover:scale-110"
                  data-testid={`star-${n}`}
                >
                  <Star
                    className={`w-8 h-8 ${n <= rating ? 'text-[#EA580C] fill-[#EA580C]' : 'text-slate-300'}`}
                  />
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label>Commento</Label>
            <Textarea
              data-testid="review-comment-input"
              value={commento}
              onChange={(e) => setCommento(e.target.value)}
              rows={4}
              required
              placeholder="Descrivi la tua esperienza..."
              className="rounded-lg"
            />
          </div>
          <Button type="submit" data-testid="submit-review-btn" disabled={loading} className="w-full bg-[#EA580C] hover:bg-[#EA580C]/90 text-white rounded-lg">
            {loading ? 'Invio...' : 'Invia Recensione'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const SiteReviewSection = ({ onUpdate }) => {
  const [myReviews, setMyReviews] = useState([]);
  const [rating, setRating] = useState(5);
  const [commento, setCommento] = useState('');
  const [tipo, setTipo] = useState('platform');
  const [loading, setLoading] = useState(false);

  const loadMine = async () => {
    try {
      const { data } = await axios.get(`${API}/site-reviews/me`, { withCredentials: true });
      setMyReviews(data);
    } catch (e) {
      // Non-blocking: site reviews are optional. Log for diagnostics, no toast (avoids noisy UI on first visit).
      console.warn('Failed to load my site reviews:', e?.response?.status, e?.message);
    }
  };

  React.useEffect(() => { loadMine(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/site-reviews`, { rating, commento, tipo }, { withCredentials: true });
      toast.success('Grazie per la recensione!');
      setCommento(''); setRating(5);
      loadMine();
      if (onUpdate) onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-semibold text-[#0F172A]">Recensisci il sito</h2>
        <p className="text-sm text-slate-600">Aiutaci a migliorare. La tua recensione apparirà sulla home page del sito.</p>
      </div>
      <Card className="bg-white border border-slate-200 rounded-xl" data-testid="site-review-form">
        <CardContent className="p-6 space-y-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="mb-2 block">Recensisci</Label>
              <RadioGroup value={tipo} onValueChange={setTipo}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="platform" id="sr-platform" data-testid="sr-tipo-platform" />
                  <Label htmlFor="sr-platform">La piattaforma RISTRUTTURA CASA ROMA</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="consultation" id="sr-cons" data-testid="sr-tipo-consultation" />
                  <Label htmlFor="sr-cons">La consulenza telefonica ricevuta</Label>
                </div>
              </RadioGroup>
            </div>
            <div>
              <Label className="mb-2 block">Valutazione</Label>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" onClick={() => setRating(n)} className="transition-transform hover:scale-110" data-testid={`sr-star-${n}`}>
                    <Star className={`w-8 h-8 ${n <= rating ? 'text-[#EA580C] fill-[#EA580C]' : 'text-slate-300'}`} />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>Commento</Label>
              <Textarea data-testid="sr-comment" value={commento} onChange={(e) => setCommento(e.target.value)} required rows={4} className="rounded-lg" />
            </div>
            <Button type="submit" data-testid="sr-submit" disabled={loading} className="bg-[#EA580C] hover:bg-[#EA580C]/90 text-white rounded-lg">
              {loading ? 'Invio...' : 'Invia recensione'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {myReviews.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-[#0F172A]">Le mie recensioni del sito</h3>
          {myReviews.map((r) => (
            <Card key={r.id} className="bg-white border border-slate-200 rounded-xl" data-testid={`my-sr-${r.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{r.tipo === 'platform' ? 'Piattaforma' : 'Consulenza'}</span>
                  <div className="flex">
                    {[1,2,3,4,5].map((n) => (<Star key={n} className={`w-3.5 h-3.5 ${n <= r.rating ? 'text-[#EA580C] fill-[#EA580C]' : 'text-slate-300'}`} />))}
                  </div>
                </div>
                <p className="text-sm text-slate-700">{r.commento}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProprietarioDashboard;
